import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '@app/_services';
import { SharedService } from '@app/_services/shared.service';
import { AiTokensService } from './ai-tokens.service';
import { AiTokensUsageService } from '@app/_services/ai-tokens-usage';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CurrencyConvertPipe } from '@app/shared/pipes';

@UntilDestroy()
@Component({
    selector: 'app-page-ai-tokens',
    templateUrl: './page-ai-tokens.component.html',
    styleUrls: ['./page-ai-tokens.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule, CurrencyConvertPipe],
    providers: [AiTokensService]
})
export class PageAiTokensComponent implements OnInit {
    balance: any = null;
    subscriptionBalance: { remaining: number; total: number; used: number } | null = null;
    overallUsage: { total: number; used: number; remaining: number } | null = null;
    packages: any[] = [];
    purchaseHistory: any[] = [];
    isLoading: boolean = true;
    isPurchasing: boolean = false;
    activeTab: 'packages' | 'history' = 'packages';
    Math = Math; // Expose Math to template

    constructor(
        private authService: AuthenticationService,
        private aiTokensService: AiTokensService,
        private aiTokensUsageService: AiTokensUsageService,
        private sharedService: SharedService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        
        // Load balance
        this.aiTokensService.getBalance().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    this.balance = response.data;
                }
            },
            error: (error) => {
                console.error('Error loading balance:', error);
            }
        });

        // Load subscription plan balance (from new_subscriptions)
        this.aiTokensUsageService.getMyUsage().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    const subTotal = response.data.subscriptionTokensTotal || 0;
                    const subRemaining = response.data.subscriptionTokensRemaining || 0;
                    this.subscriptionBalance = {
                        total: subTotal,
                        remaining: subRemaining,
                        used: Math.max(0, subTotal - subRemaining)
                    };
                    
                    // Store overall usage data (subscription + purchased)
                    this.overallUsage = {
                        total: response.data.aiTokensLimit || 0,
                        used: response.data.aiTokensUsed || 0,
                        remaining: response.data.remaining || 0
                    };
                }
            },
            error: (error) => {
                console.error('Error loading subscription balance:', error);
            }
        });

        // Load packages filtered by current country
        const currentCountry = this.sharedService.selectedCountry;
        const countryName = currentCountry?.name?.en || currentCountry?.code || null;
        this.aiTokensService.getPackages(countryName).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.success && response.data) {
                    this.packages = response.data;
                }
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Error loading packages:', error);
            }
        });

        // Load purchase history
        this.aiTokensService.getPurchaseHistory().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    this.purchaseHistory = response.data;
                }
            },
            error: (error) => {
                console.error('Error loading history:', error);
            }
        });
    }

    setActiveTab(tab: 'packages' | 'history'): void {
        this.activeTab = tab;
    }

    getPackageName(pkg: any): string {
        if (!pkg?.name) return '-';
        const lang = this.translate.currentLang || 'en';
        return pkg.name[lang] || pkg.name.en || pkg.name.ar || '-';
    }

    getPackageDescription(pkg: any): string {
        if (!pkg?.description) return '';
        const lang = this.translate.currentLang || 'en';
        return pkg.description[lang] || pkg.description.en || pkg.description.ar || '';
    }

    getCurrencyCode(currency: any): string {
        if (!currency) return 'BHD';
        // If it's already a string (code), return it
        if (typeof currency === 'string') return currency;
        // If it's an object with en/ar properties, extract the code
        // The currency should now be stored as code (e.g., "BHD", "AED", "SAR")
        return currency.en || currency.ar || 'BHD';
    }

    purchasePackage(pkg: any): void {
        if (this.isPurchasing) return;

        // Check if user is logged in
        const currentUser = this.authService.currentUserValue;
        if (!currentUser) {
            this.toastr.error(this.translate.instant('please_login_first') || 'Please login first');
            this.router.navigate(['/login']);
            return;
        }

        // Check if user is a customer (not a vendor) - AI features not yet available for customers
        if (currentUser && !currentUser.isVendor) {
            this.toastr.error(
                this.translate.instant('ai_tokens.not_available_for_customers'),
                this.translate.instant('ai_tokens.feature_coming_soon')
            );
            return;
        }
        
        // Navigate to checkout page - all data will be fetched from database
        // User is authenticated, package ID is in route
        this.router.navigate(['/checkout-s', pkg._id, 'ai-token-purchase']);
    }

    formatDate(date: string | Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    formatNumber(num: number): string {
        return num?.toLocaleString() || '0';
    }

    getUsagePercentage(): number {
        if (!this.balance || !this.balance.aiTokensLimit) return 0;
        return Math.round((this.balance.aiTokensUsed / this.balance.aiTokensLimit) * 100);
    }

    getTotalUsagePercentage(): number {
        const capacity = this.getTotalTokensCapacity();
        if (!capacity || capacity === 0) return 0;
        const used = this.getTotalTokensUsed();
        const percentage = (used / capacity) * 100;
        return Math.round(percentage);
    }

    getPurchasedUsagePercentage(): number {
        if (!this.balance || !this.balance.aiTokensLimit || this.balance.aiTokensLimit === 0) return 0;
        const used = this.balance.aiTokensUsed || 0;
        const limit = this.balance.aiTokensLimit;
        const percentage = (used / limit) * 100;
        return Math.round(percentage);
    }

    getSubscriptionUsagePercentage(): number {
        if (!this.subscriptionBalance || !this.subscriptionBalance.total || this.subscriptionBalance.total === 0) return 0;
        const used = this.subscriptionBalance.used || 0;
        const total = this.subscriptionBalance.total;
        const percentage = (used / total) * 100;
        return Math.round(percentage);
    }

    getPurchasedTokensUsed(): number {
        if (!this.balance || !this.balance.aiTokensLimit) return 0;
        // Cap the used amount at the purchased limit
        return Math.min(this.balance.aiTokensUsed || 0, this.balance.aiTokensLimit);
    }

    getPurchasedTokensRemaining(): number {
        if (!this.balance || !this.balance.aiTokensLimit) return 0;
        // Show 0 if overused, otherwise show actual remaining
        return Math.max(0, this.balance.remaining || 0);
    }

    getTotalRemainingBalance(): number {
        return this.overallUsage?.remaining || 0;
    }

    getTotalTokensUsed(): number {
        return this.overallUsage?.used || 0;
    }

    getTotalTokensCapacity(): number {
        return this.overallUsage?.total || 0;
    }

    isTotalBalanceOverused(): boolean {
        return this.getTotalRemainingBalance() < 0;
    }
}
